import { OmneaError } from '@omnea-digital/toolbox'
import { useQueryClient } from '@tanstack/react-query'
import isNil from 'lodash/isNil'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DeepPartial } from 'utils/utiltyTypes/utilityTypes'
import { BlockTypeEnum } from '@omnea-app/core/blocks'
import { CoreHttpClient } from '@omnea-app/core/coreHttpClient'
import { ApprovalAction, RequestStepAction } from '@omnea-app/core/stages/utilEnums'
import { GraphNode } from '@omnea-app/core/workflow-engine/Graph'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/legacy/utils/error-handling/throwifApiError'
import { assertNever } from '@omnea-app/legacy/utils/type-guard/assertNever'
import { useGetRequestStepTaskErrors } from '../../../request-step/requestStepTaskErrors/useGetRequestStepTaskErrors'
import { RequestDetailQueryKey } from '../../../useRequestDetail'
import { useSetBlockSkip } from '../../../useSetBlockSkip'
import { useSkipConditions } from '../../../useSkipConditions'
import { useUpdateRequestConfig } from '../../../useUpdateRequestConfig'
import { RequestStep } from '../../types'
import { futureSkippableBlockTypes } from './getAllowedActions'
import { TimelineBlockFull } from './types'
import { useUpdateStepData } from './useUpdateRequestStep'

const PO_POLLING_REFRESH_DELAY_MS = 90000

export type SupportedAction =
  | RequestStepAction.FORCE_APPROVE
  | RequestStepAction.HTTP_RETRY
  | RequestStepAction.DATA_TRANSFORMATION_RETRY
  | RequestStepAction.ROLLBACK
  | RequestStepAction.SKIP
  | RequestStepAction.RERUN_ACCOUNTING_SYNC
  | RequestStepAction.UN_SKIP
  | RequestStepAction.POLL
  | RequestStepAction.UPDATE_CONFIG
  | RequestStepAction.SKIP_CONDITIONS

export const useRequestActions = ({ requestId }: { requestId: string }) => {
  const { t } = useTranslation('requests')
  const { enqueueSnackbar } = useSnackbar()
  const [loadingStep, setLoadingStep] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { mutateAsync: updateDataMutation } = useUpdateStepData({ requestId })
  const { getToken, user } = useAuthenticatedUser()
  const { refetch: refetchRequestStepTaskErrors } = useGetRequestStepTaskErrors({ requestId })
  const { mutateAsync: setBlockSkip, isPending: isBlockSkipPending } = useSetBlockSkip()
  const { mutateAsync: updateRequestConfig } = useUpdateRequestConfig()
  const { mutateAsync: skipConditionsMutation } = useSkipConditions()

  async function rollback(block: TimelineBlockFull | undefined) {
    if ((block && isFuture(block?.step)) || isNil(block?.requestStepId)) {
      enqueueSnackbar(t('timeline-overview.step-rollback-error-message'), {
        variant: 'error',
      })
    } else {
      const authorization = await getToken()
      const coreClient = new CoreHttpClient({ authorization })
      const { error } = await coreClient.POST('/rollback-request-to-step', {
        body: { requestId, requestStepId: block?.requestStepId },
      })
      throwIfApiError(error)
      enqueueSnackbar(t('timeline-overview.step-rollback-message'), {
        variant: 'success',
      })
      void queryClient.invalidateQueries({ queryKey: [RequestDetailQueryKey, requestId] })
    }
  }

  const skip = async (block: TimelineBlockFull, comment?: string) => {
    return await setBlockSkip({
      requestId,
      blockId: block.id,
      skip: true,
      comment,
    })
  }

  const unSkip = async (block: TimelineBlockFull) => {
    return await setBlockSkip({
      requestId,
      blockId: block.id,
      skip: false,
    })
  }

  const httpRetry = async (block: TimelineBlockFull) => {
    if (!isFuture(block.step)) {
      await updateDataMutation({
        body: {
          input: {
            data: {},
            id: block.requestStepId,
            actionBy: user.id,
            action: RequestStepAction.HTTP_RETRY,
          },
        },
        setLoadingStep,
      })
    }
  }

  const dataTransformationRetry = async (block: TimelineBlockFull) => {
    if (!isFuture(block.step)) {
      await updateDataMutation({
        body: {
          input: {
            data: {},
            id: block.requestStepId,
            actionBy: user.id,
            action: RequestStepAction.DATA_TRANSFORMATION_RETRY,
          },
        },
        setLoadingStep,
      })
      await refetchRequestStepTaskErrors()
    }
  }

  const poll = async (block: TimelineBlockFull) => {
    if (!isFuture(block.step)) {
      try {
        await updateDataMutation({
          body: {
            input: {
              data: {},
              id: block.requestStepId,
              actionBy: user.id,
              action: RequestStepAction.POLL,
            },
          },
          setLoadingStep,
        })
      } catch (error) {
        // If the API returns 404, we throw instead of returning an error
        setLoadingStep(null)
        if (error instanceof OmneaError && !isNil(error.suggestion)) {
          enqueueSnackbar(t('timeline-overview.step-poll-error-message', { suggestion: error.suggestion }), {
            variant: 'error',
          })
          return
        }
        enqueueSnackbar(t('timeline-overview.step-poll-error-message-generic'), {
          variant: 'error',
        })

        return
      }

      await refetchRequestStepTaskErrors()

      void queryClient.invalidateQueries({ queryKey: ['externalOperationStatus', block.requestStepId] })

      enqueueSnackbar(
        block.type === BlockTypeEnum.CREATE_PO
          ? t('timeline-overview.step-poll-message-createpo')
          : t('timeline-overview.step-poll-message'),
        {
          variant: 'success',
        }
      )

      // Refetch request data after 90 seconds to pick up accounting updates (CreatePO only)
      if (block.type === BlockTypeEnum.CREATE_PO) {
        setTimeout(() => {
          void queryClient.invalidateQueries({ queryKey: [RequestDetailQueryKey, requestId] })
        }, PO_POLLING_REFRESH_DELAY_MS)
      }
    }
  }

  const rerunAccountingSync = async (block: TimelineBlockFull) => {
    if (!isFuture(block.step)) {
      await updateDataMutation({
        body: {
          input: {
            data: {},
            id: block.requestStepId,
            actionBy: user.id,
            action: RequestStepAction.RERUN_ACCOUNTING_SYNC,
          },
        },
        setLoadingStep,
      })
      await refetchRequestStepTaskErrors()
    }
  }

  const forceApprove = async (block: TimelineBlockFull, comment?: string) => {
    if (!isFuture(block.step)) {
      await updateDataMutation({
        body: {
          input: {
            data: { comment },
            id: block.requestStepId,
            actionBy: user.id,
            action: ApprovalAction.ForceApprove,
          },
        },
        setLoadingStep,
      })

      enqueueSnackbar(t('timeline-overview.step-force-approved-message'), {
        variant: 'success',
      })
    }
  }

  const skipConditions = async (block: TimelineBlockFull) => {
    await skipConditionsMutation({ requestId, blockId: block.id })
  }

  const updateConfig = async (block: TimelineBlockFull) => {
    try {
      await updateRequestConfig({
        requestId,
        blockId: block.id,
        updateRequest: true,
        updateRequestStep: block.step !== null,
      })

      enqueueSnackbar(t('timeline-overview.step-config-updated-message'), {
        variant: 'success',
      })

      void queryClient.invalidateQueries({ queryKey: [RequestDetailQueryKey, requestId] })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Update failed'
      enqueueSnackbar(errorMessage, {
        variant: 'error',
      })
    }
  }

  const onAction = async (block: TimelineBlockFull | undefined, action: SupportedAction | null, comment?: string) => {
    const isActionThatDoesNotRequireBlock = action && action === RequestStepAction.ROLLBACK
    if (isActionThatDoesNotRequireBlock) {
      return await rollback(block)
    }

    if (isNil(block) || isNil(action)) {
      return
    }

    if (!isNil(loadingStep) || isBlockSkipPending) {
      enqueueSnackbar(t('timeline-overview.no-simultaneous-actions'))
      return
    }

    switch (action) {
      case RequestStepAction.FORCE_APPROVE:
        return await forceApprove(block, comment)
      case RequestStepAction.HTTP_RETRY:
        return await httpRetry(block)
      case RequestStepAction.DATA_TRANSFORMATION_RETRY:
        return await dataTransformationRetry(block)
      case RequestStepAction.SKIP:
        return await skip(block, comment)
      case RequestStepAction.RERUN_ACCOUNTING_SYNC:
        return await rerunAccountingSync(block)
      case RequestStepAction.UN_SKIP:
        return await unSkip(block)
      case RequestStepAction.POLL:
        return await poll(block)
      case RequestStepAction.UPDATE_CONFIG:
        return await updateConfig(block)
      case RequestStepAction.SKIP_CONDITIONS:
        return await skipConditions(block)
      default:
        assertNever(action)
        console.warn(`Unhandled action: ${action}`)
    }
  }

  return {
    loadingStep,
    onAction,
  }
}

const isFuture = (step: RequestStep | null) => {
  return isNil(step?.state)
}

type SkippableBlock = {
  metadata: {
    config: {
      payload: {
        skip?: {
          skippedBy: string
          skippedAt: string
        }
      }
    }
  }
}

/**
 * Typeguard to ensure the model is skippable if not yet running
 * @param model
 * @param canvasModelId
 * @returns true if skippable (currently only Approval and Supplier Portal)
 */
export const isFutureSkippable = (node: DeepPartial<GraphNode>): node is SkippableBlock => {
  const type = node?.metadata?.type
  return type ? futureSkippableBlockTypes.includes(type) : false
}
